/**
 * 帧协议与 libp2p 业务协议常量 —— 浏览器与 Node 服务共用同一份实现。
 *
 * 帧格式（ARCH §8.4.2）：**4 字节大端长度前缀 + UTF-8 JSON**。
 */

/**
 * libp2p 业务协议 ID。
 *
 * 注意 FEED 与 FEED_REQ 是**同一份广场逻辑的两条协议**，语义严格分离（T03 缺陷4 修复）：
 *  - `FEED`     —— **只承载「订阅长连接」**。客户端 dial 后**一个字节都不写**，仅读取实时推送；
 *                  写半端因此永不关闭，服务端才可对其登记为广播目标（`feedAdd`）。
 *  - `FEED_REQ` —— **只承载「请求/应答」**（history / publish）。短命流，服务端回一次即关写半端；
 *                  短命流**绝不**进入订阅广播集，从机制上根除「向已关闭流二次 sink」。
 *
 * 铁律：**任何请求/应答一律走 `FEED_REQ`，绝不在 `FEED` 上发请求** ——
 * 否则一次性回包会关闭订阅流写半端，实时推送随即失效。
 */
export const PROTOCOL = {
  DIRECTORY: '/meshlink/directory/1.0.0',
  RELAY_MESSAGE: '/meshlink/relay-message/1.0.0',
  RELAY_DIRECTORY: '/meshlink/relay-directory/1.0.0',
  FEED: '/meshlink/feed/1.0.0',
  FEED_REQ: '/meshlink/feed-req/1.0.0',
  CONTENT: '/meshlink/content/1.0.0',
  COMMENT: '/meshlink/comment/1.0.0',
  COMMENT_REQ: '/meshlink/comment-req/1.0.0',
  GROUP: '/meshlink/group/1.0.0',
  GROUP_REQ: '/meshlink/group-req/1.0.0',
} as const;

export type ProtocolId = (typeof PROTOCOL)[keyof typeof PROTOCOL];

/**
 * 业务协议注册时的必带选项。
 * libp2p 1.x 默认不在「瞬态连接」（经中继的 circuit 连接）上运行 handler，
 * 不声明此项会导致中继转发来的消息无人处理 —— 原型踩过的坑（ARCH §8.4.1）。
 */
export const TRANSIENT_HANDLER_OPTS = { runOnTransientConnection: true } as const;

/** 单帧负载上限（16MB，超出直接丢弃，防内存打爆）。 */
export const MAX_FRAME_BYTES = 16 * 1024 * 1024;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** 编码一帧：4 字节大端长度 + UTF-8 JSON。 */
export function encodeFrame(obj: unknown): Uint8Array {
  const data = encoder.encode(JSON.stringify(obj));
  const out = new Uint8Array(4 + data.length);
  new DataView(out.buffer).setUint32(0, data.length, false);
  out.set(data, 4);
  return out;
}

/** 解一帧，返回消息与剩余字节；不足一整帧返回 null。 */
export function decodeFrame(buffer: Uint8Array): { msg: unknown; rest: Uint8Array } | null {
  if (buffer.length < 4) return null;
  const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
  const len = view.getUint32(0, false);
  if (len > MAX_FRAME_BYTES) {
    throw new RangeError(`frame too large: ${len}`);
  }
  if (buffer.length < 4 + len) return null;
  const msg = JSON.parse(decoder.decode(buffer.subarray(4, 4 + len))) as unknown;
  return { msg, rest: buffer.subarray(4 + len) };
}

/** 从缓冲区中一次性抽出所有完整帧。 */
export function drainFrames(buffer: Uint8Array): { frames: unknown[]; rest: Uint8Array } {
  const frames: unknown[] = [];
  let rest = buffer;
  for (;;) {
    const r = decodeFrame(rest);
    if (!r) break;
    frames.push(r.msg);
    rest = r.rest;
  }
  return { frames, rest: rest.length === 0 ? new Uint8Array(0) : rest.slice() };
}

/**
 * 流式帧解码器：把任意分块的字节流喂进来，吐出完整帧。
 * 解决 TCP/WS 粘包拆包问题。
 */
export class FrameDecoder {
  private buffer: Uint8Array = new Uint8Array(0);

  /** 喂入一块字节，返回本次解析出的全部完整帧。 */
  push(chunk: Uint8Array): unknown[] {
    const merged = new Uint8Array(this.buffer.length + chunk.length);
    merged.set(this.buffer);
    merged.set(chunk, this.buffer.length);
    const { frames, rest } = drainFrames(merged);
    this.buffer = rest;
    return frames;
  }

  /** 当前尚未凑齐一帧的残余字节数（调试用）。 */
  get pendingBytes(): number {
    return this.buffer.length;
  }

  /**
   * 便捷入口：base64 帧串 → 全部帧。
   * 中继邮箱里存的 `data` 就是 `encodeFrame(envelope)` 的 base64，用这个方法一次解出。
   */
  static fromBase64(b64: string): unknown[] {
    return drainFrames(b64ToBytes(b64)).frames;
  }

  reset(): void {
    this.buffer = new Uint8Array(0);
  }
}

// ─────────────────────────────────────────────────────────────
// base64 辅助（同构：浏览器与 Node 20+ 都有 btoa/atob）
// ─────────────────────────────────────────────────────────────

/** Uint8Array → base64。 */
export function bytesToB64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** base64 → Uint8Array。 */
export function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}
